import { useEffect, useMemo, useRef, useState } from "react";
import { lightenColor, darkenColor } from "../theme";
import GuaranteeSeal from "./GuaranteeSeal";
import PolicyModal from "./PolicyModal";
import { asset } from "../../assetUrl";
import "./Template2.css";
import { BRAND_DEFAULT_SVG } from "../../brandDefaultLogo";
import { sanitizeHtml } from "../../sanitizeHtml";

/* Default brand logo (shown until the creator sets their own) + default course
   cover image. Both are overridden by data when the creator uploads one. */
// Course-landing pages are light-only (no dark: classes anywhere in this
// folder), so the light placeholder is the right one. The old default was
// FlexiLogo.svg — OUR mark, on a CUSTOMER's sales page, which contradicts the
// rule stated on BrandLogo — and it was a 100 KB "SVG" that turned out to be a
// single <rect> filled with a base64 PNG, so it could not be recoloured either.
const DEFAULT_LOGO = BRAND_DEFAULT_SVG;
const DEFAULT_COURSE_IMAGE = asset("/images/course-default.svg");

/* ──────────────────────────────────────────────────────────────────────────
   Template 2 — "Editorial / Coal + Amber"
   Pixel-exact JSX port of resources/views/fe/courselanding2.blade.php.
   All visual CSS lives in Template2.css scoped under `.clp2-root`. Bootstrap
   (grid + icons) is not loaded in this app, so utilities are reproduced in CSS
   and every `bi bi-*` icon is an inline SVG below.

   The Blade derived a wide palette from the theme primary. The wrapper supplies
   the standard `--cl-*` vars; the extra derived shades the Blade used (ivory =
   lighten 92, sand = lighten 75, indigo-soft = lighten 20, amber = lighten 35,
   amber-hover = darken 10, amber-light = lighten 80) are computed here from
   `data.primary` and set as inline custom props on the root, so the look stays
   data-driven per course exactly as the server-rendered Blade did.
   ────────────────────────────────────────────────────────────────────────── */

/* ── Inline SVG icons (Bootstrap Icons equivalents) ───────────────────────── */
const Icon = {
  mortarboardFill: (p) => (
    <svg viewBox="0 0 16 16" {...p} className={["bi-svg", p.className].filter(Boolean).join(" ")}>
      <path d="M8.211 2.047a.5.5 0 0 0-.422 0l-7.5 3.5a.5.5 0 0 0 .025.917l7.5 3a.5.5 0 0 0 .372 0L14 7.14V13a1 1 0 0 0-1 1v2h3v-2a1 1 0 0 0-1-1V6.739l.686-.275a.5.5 0 0 0 .025-.917l-7.5-3.5Z" />
      <path d="M4.176 9.032a.5.5 0 0 0-.656.327l-.5 1.7a.5.5 0 0 0 .294.605l4.5 1.8a.5.5 0 0 0 .372 0l4.5-1.8a.5.5 0 0 0 .294-.605l-.5-1.7a.5.5 0 0 0-.656-.327L8 10.466 4.176 9.032Z" />
    </svg>
  ),
  arrowRight: (p) => (
    <svg viewBox="0 0 16 16" {...p} className={["bi-svg", p.className].filter(Boolean).join(" ")}>
      <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z" />
    </svg>
  ),
  playFill: (p) => (
    <svg viewBox="0 0 16 16" {...p} className={["bi-svg", p.className].filter(Boolean).join(" ")}>
      <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z" />
    </svg>
  ),
  book: (p) => (
    <svg viewBox="0 0 16 16" {...p} className={["bi-svg", p.className].filter(Boolean).join(" ")}>
      <path d="M1 2.828c.885-.37 2.154-.769 3.388-.893 1.33-.134 2.458.063 3.112.752v9.746c-.935-.53-2.12-.603-3.213-.493-1.18.12-2.37.461-3.287.811V2.828zm7.5-.141c.654-.689 1.782-.886 3.112-.752 1.234.124 2.503.523 3.388.893v9.923c-.918-.35-2.107-.692-3.287-.81-1.094-.111-2.278-.039-3.213.492V2.687zM8 1.783C7.015.936 5.587.81 4.287.94c-1.514.153-3.042.672-3.994 1.105A.5.5 0 0 0 0 2.5v11a.5.5 0 0 0 .707.455c.882-.4 2.303-.881 3.68-1.02 1.409-.142 2.59.087 3.223.877a.5.5 0 0 0 .78 0c.633-.79 1.814-1.019 3.222-.877 1.378.139 2.8.62 3.681 1.02A.5.5 0 0 0 16 13.5v-11a.5.5 0 0 0-.293-.455c-.952-.433-2.48-.952-3.994-1.105C10.413.809 8.985.936 8 1.783z" />
    </svg>
  ),
  starFill: (p) => (
    <svg viewBox="0 0 16 16" {...p} className={["bi-svg", p.className].filter(Boolean).join(" ")}>
      <path d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z" />
    </svg>
  ),
  fileEarmarkText: (p) => (
    <svg viewBox="0 0 16 16" {...p} className={["bi-svg", p.className].filter(Boolean).join(" ")}>
      <path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5z" />
      <path d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5L9.5 0zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z" />
    </svg>
  ),
  playCircleFill: (p) => (
    <svg viewBox="0 0 16 16" {...p} className={["bi-svg", p.className].filter(Boolean).join(" ")}>
      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM6.79 5.093A.5.5 0 0 0 6 5.5v5a.5.5 0 0 0 .79.407l3.5-2.5a.5.5 0 0 0 0-.814l-3.5-2.5z" />
    </svg>
  ),
  shieldCheck: (p) => (
    <svg viewBox="0 0 16 16" {...p} className={["bi-svg", p.className].filter(Boolean).join(" ")}>
      <path d="M5.338 1.59a61.44 61.44 0 0 0-2.837.856.481.481 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.725 10.725 0 0 0 2.287 2.233c.346.244.652.42.893.533.12.057.218.095.293.118a.55.55 0 0 0 .101.025.615.615 0 0 0 .1-.025c.076-.023.174-.061.294-.118.24-.113.547-.29.893-.533a10.726 10.726 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.30 8.531 1.1 8 1.1c-.531 0-1.552.2-2.662.49zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.775 11.775 0 0 1-2.517 2.453 7.159 7.159 0 0 1-1.048.625c-.28.132-.581.24-.829.24s-.548-.108-.829-.24a7.158 7.158 0 0 1-1.048-.625 11.777 11.777 0 0 1-2.517-2.453C1.379 10.485-.004 7.167.592 2.692A1.54 1.54 0 0 1 1.636 1.43a62.468 62.468 0 0 1 2.887-.87z" />
      <path d="M10.854 5.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 7.793l2.646-2.647a.5.5 0 0 1 .708 0z" />
    </svg>
  ),
  twitterX: (p) => (
    <svg viewBox="0 0 16 16" {...p} className={["bi-svg", p.className].filter(Boolean).join(" ")}>
      <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865l8.875 11.633Z" />
    </svg>
  ),
  youtube: (p) => (
    <svg viewBox="0 0 16 16" {...p} className={["bi-svg", p.className].filter(Boolean).join(" ")}>
      <path d="M8.051 1.999h.089c.822.003 4.987.033 6.11.335a2.01 2.01 0 0 1 1.415 1.42c.101.38.172.883.22 1.402l.01.104.022.26.008.104c.065.914.073 1.77.074 1.957v.075c-.001.194-.01 1.108-.082 2.06l-.008.105-.009.104c-.05.572-.124 1.14-.235 1.558a2.007 2.007 0 0 1-1.415 1.42c-1.16.312-5.569.334-6.18.335h-.142c-.309 0-1.587-.006-2.927-.052l-.17-.006-.087-.004-.171-.007-.171-.007c-1.11-.049-2.167-.128-2.654-.26a2.007 2.007 0 0 1-1.415-1.419c-.111-.417-.185-.986-.235-1.558L.09 9.82l-.008-.104A31.4 31.4 0 0 1 0 7.68v-.123c.002-.215.01-.958.064-1.778l.007-.103.003-.052.008-.104.022-.26.01-.104c.048-.519.119-1.023.22-1.402a2.007 2.007 0 0 1 1.415-1.42c.487-.13 1.544-.21 2.654-.26l.17-.007.172-.006.086-.003.171-.007A99.788 99.788 0 0 1 7.858 2h.193zM6.4 5.209v4.818l4.157-2.408L6.4 5.209z" />
    </svg>
  ),
  linkedin: (p) => (
    <svg viewBox="0 0 16 16" {...p} className={["bi-svg", p.className].filter(Boolean).join(" ")}>
      <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z" />
    </svg>
  ),
  instagram: (p) => (
    <svg viewBox="0 0 16 16" {...p} className={["bi-svg", p.className].filter(Boolean).join(" ")}>
      <path d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.917 3.917 0 0 0-1.417.923A3.927 3.927 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.916 3.916 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.926 3.926 0 0 0-.923-1.417A3.911 3.911 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0h.003zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599.28.28.453.546.598.92.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.47 2.47 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.478 2.478 0 0 1-.92-.598 2.48 2.48 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233 0-2.136.008-2.388.046-3.231.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92.28-.28.546-.453.92-.598.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045v.002zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92zm-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217zm0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334z" />
    </svg>
  ),
  facebook: (p) => (
    <svg viewBox="0 0 16 16" {...p} className={["bi-svg", p.className].filter(Boolean).join(" ")}>
      <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z" />
    </svg>
  ),
};

/* Heading word-index-1 accent (Blade wraps the 2nd word). */
function accentWords(text, color) {
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  return words.map((word, i) =>
    i === 1 ? (
      <span key={i} style={{ color }}>
        {word}{" "}
      </span>
    ) : (
      <span key={i}>{word} </span>
    )
  );
}

const Stars = ({ rating, max = 5 }) => {
  const r = Math.round(Number(rating) || 0);
  return Array.from({ length: max }, (_, i) => (i < r ? "★" : "☆")).join("");
};

export default function Template2({ data }) {
  const {
    course = {},
    logo = null,
    header = {},
    skill = {},
    course_include = {},
    prerequisites = {},
    testimonial = {},
    money_back = {},
    faq = {},
    social = {},
    policies = {},
    labels = {},
    video = {},
    video_embed = null,
    modules = [],
    lessonsByModule = {},
    moduleCount = 0,
    lessonCount = 0,
    instructor = null,
    rating = 0,
    members = [],
    memberCount = 0,
    enrollText = "Enroll now",
    enrollLink = "#",
    template_id = 2,
    primary = "#1daa61",
  } = data || {};

  const lbl = (k, d) =>
    String(labels?.[k] || "").trim() !== "" ? labels[k] : d;

  const courseName = course.name || course.title || "";
  const heroTitle =
    String(header.headline || "").trim() !== "" ? header.headline : courseName;
  const heroSub =
    String(header.sub_headline || "").trim() !== ""
      ? header.sub_headline
      : course.short_description || "";
  // Nav brand logo: creator's logo when set, else the default brand logo.
  const logoImg = String(logo || "").trim() !== "" ? logo : DEFAULT_LOGO;
  // Course cover (hero visual + enroll/curriculum): uploaded image, else default.
  const coverImg = course.funnel_page_image || DEFAULT_COURSE_IMAGE;

  const lessonsFor = (cid) => lessonsByModule?.[String(cid)] || [];

  const headerVisible = header.status === undefined || header.status;

  // The "Course includes" toggle controls the curriculum ("What's inside the
  // course") section. Default ON when never configured; hide only when the
  // creator explicitly turns it off.
  const showCurriculum =
    course_include.status === undefined ? true : !!course_include.status;

  // Extra derived theme shades the Blade computed but the wrapper doesn't supply.
  const themeStyle = useMemo(
    () => ({
      "--clp2-indigo-soft": lightenColor(primary, 20),
      "--clp2-amber": lightenColor(primary, 35),
      "--clp2-amber-hover": darkenColor(primary, 10),
      "--clp2-amber-light": lightenColor(primary, 80),
      "--clp2-ivory": lightenColor(primary, 92),
      "--clp2-ivory-2": lightenColor(primary, 85),
      "--clp2-sand": lightenColor(primary, 75),
    }),
    [primary]
  );

  // ── Curriculum accordion (first module open) ──
  const [openModule, setOpenModule] = useState(0);
  // ── FAQ accordion ──
  const [openFaq, setOpenFaq] = useState(null);
  // ── Footer policy popup: "privacy" | "terms" | null ──
  const [openPolicy, setOpenPolicy] = useState(null);
  const hasPrivacy = String(policies?.privacy || "").trim() !== "";
  const hasTerms = String(policies?.terms || "").trim() !== "";
  // ── Hero video ──
  const [videoPlaying, setVideoPlaying] = useState(false);
  const autoplaySrc = (() => {
    if (!video_embed) return null;
    const base =
      video_embed +
      (video_embed.indexOf("?") !== -1 ? "&" : "?") +
      "enablejsapi=1";
    if (base.indexOf("autoplay=1") !== -1) return base;
    return base + (base.indexOf("?") !== -1 ? "&" : "?") + "autoplay=1";
  })();

  // ── Scroll reveal + progress bar ──
  const rootRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.07 }
    );
    root.querySelectorAll(".reveal").forEach((el) => observer.observe(el));

    const onScroll = () => {
      const pct =
        (window.scrollY /
          (document.body.scrollHeight - window.innerHeight)) *
        100;
      setProgress(Number.isFinite(pct) ? pct : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, [data]);

  // Smooth in-page scroll.
  const onAnchorClick = (e, href) => {
    const target = rootRef.current?.querySelector(href);
    if (target) {
      e.preventDefault();
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const heroStyle = header.header_bg_image
    ? {
        backgroundImage: `url('${header.header_bg_image}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : undefined;

  const guaranteeStyle = money_back.money_back_bg_image
    ? {
        backgroundImage: `url('${money_back.money_back_bg_image}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : undefined;

  const hasBenefits =
    (skill?.status && skill?.list?.length) ||
    (course_include?.status && course_include?.list?.length) ||
    (prerequisites?.status && prerequisites?.list?.length);

  const guaranteeVisible =
    money_back?.status &&
    (String(money_back.heading || "").trim() !== "" ||
      String(money_back.description || "").trim() !== "");

  return (
    <div className="clp2-root" ref={rootRef} style={themeStyle}>
      <div
        id="scroll-progress"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "3px",
          background:
            "linear-gradient(90deg, var(--amber), var(--indigo-soft))",
          zIndex: 9999,
          width: `${progress}%`,
          pointerEvents: "none",
          transition: "width .08s linear",
        }}
      />

      {/* NAV */}
      <nav className="site-nav">
        <div className="container">
          <div className="nav-inner">
            <a
              href="#home"
              className="nav-brand"
              onClick={(e) => onAnchorClick(e, "#home")}
            >
              <img src={logoImg} alt="Logo" />
            </a>
            <ul className="nav-links">
              {moduleCount && showCurriculum ? (
                <li>
                  <a
                    href="#curriculum"
                    onClick={(e) => onAnchorClick(e, "#curriculum")}
                  >
                    {lbl("curriculum", "Curriculum")}
                  </a>
                </li>
              ) : null}
              {instructor && instructor.name ? (
                <li>
                  <a
                    href="#instructor"
                    onClick={(e) => onAnchorClick(e, "#instructor")}
                  >
                    {lbl("instructor", "Instructor")}
                  </a>
                </li>
              ) : null}
              {testimonial?.status && testimonial?.list?.length ? (
                <li>
                  <a
                    href="#reviews"
                    onClick={(e) => onAnchorClick(e, "#reviews")}
                  >
                    {lbl("reviews", "Reviews")}
                  </a>
                </li>
              ) : null}
              {faq?.status && faq?.list?.length ? (
                <li>
                  <a href="#faq" onClick={(e) => onAnchorClick(e, "#faq")}>
                    FAQ
                  </a>
                </li>
              ) : null}
            </ul>
            <a
              href={enrollLink}
              className="btn btn--primary btn-nav-enroll btn-text-color"
              style={{ fontSize: ".83rem", padding: ".55rem 1.25rem" }}
            >
              <Icon.mortarboardFill className="me-2" />
              {enrollText}
            </a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="hero" id="home" style={heroStyle}>
        <div className="container">
          <div className="hero-grid">
            {/* Left: text */}
            <div className="hero-content">
              <h1 className="hero-title">
                {accentWords(heroTitle, "var(--indigo)")}
              </h1>
              {heroSub ? <p className="hero-sub">{heroSub}</p> : null}
              <div className="hero-actions">
                <a
                  href={enrollLink}
                  className="btn btn--primary btn--primary-lg btn-nav-enroll btn-text-color"
                >
                  <Icon.mortarboardFill /> {enrollText}
                </a>
                {moduleCount && showCurriculum ? (
                  <a
                    href="#curriculum"
                    className="btn btn--ghost-light btn--primary-lg d-inline-flex align-items-center gap-2"
                    onClick={(e) => onAnchorClick(e, "#curriculum")}
                  >
                    {lbl("view_curriculum", "View Curriculum")}{" "}
                    <Icon.arrowRight />
                  </a>
                ) : null}
              </div>
              {members.length || rating > 0 ? (
                <div className="hero-social-proof">
                  {members.length ? (
                    <div className="hero-avatars">
                      {members.map((m, i) =>
                        m.image ? (
                          <div className="hero-avatar" key={i}>
                            <img src={m.image} alt={m.name || ""} />
                          </div>
                        ) : (
                          <div className="hero-avatar" key={i}>
                            {String(m.name || "?").charAt(0).toUpperCase()}
                          </div>
                        )
                      )}
                    </div>
                  ) : null}
                  <div className="hero-avatars-text">
                    {members.length ? (
                      <>
                        <strong>
                          {memberCount} {lbl("members", "Members")}
                        </strong>{" "}
                        {lbl("already_enrolled", "already enrolled")}
                        <br />
                      </>
                    ) : null}
                    <span className="testi-stars">
                      <Stars rating={rating} />
                    </span>{" "}
                    <span>
                      <strong style={{ color: "rgba(255,255,255,.85)" }}>
                        {Number(rating).toFixed(1)}
                      </strong>{" "}
                      / 5 {lbl("rating", "Rating")}
                    </span>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Right: course card */}
            {headerVisible ? (
              <div className="hero-card-wrap">
                <div style={{ position: "relative" }}>
                  <div className="hero-course-card">
                    <div className="hero-card-thumb">
                      <div className="hero-card-thumb-inner">
                        {video_embed ? (
                          <div className="course-image position-relative">
                            {!videoPlaying ? (
                              <>
                                <div
                                  className="video_thumb2"
                                  onClick={() => setVideoPlaying(true)}
                                >
                                  <img
                                    className="w-100"
                                    src={video.image || coverImg}
                                    alt=""
                                  />
                                </div>
                                <div
                                  className="hero-card-thumb-icon"
                                  onClick={() => setVideoPlaying(true)}
                                >
                                  <Icon.playFill />
                                </div>
                              </>
                            ) : (
                              <div id="video_ifr_box2">
                                {(video.video_type || "") === "other" ? (
                                  <video controls autoPlay>
                                    <source src={video_embed} type="video/mp4" />
                                  </video>
                                ) : (
                                  <iframe
                                    title="course-video"
                                    src={autoplaySrc}
                                    frameBorder="0"
                                    allow="autoplay; fullscreen"
                                    allowFullScreen
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="course-image">
                            <img className="w-100" src={coverImg} alt="" />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="hero-card-body">
                      <div className="hero-card-progress-label">
                        {modules.length ? (
                          modules.slice(0, 2).map((m) => (
                            <div
                              className="d-flex align-items-center gap-3"
                              key={m.course_id}
                            >
                              <span className="icon">
                                <Icon.book />
                              </span>
                              <span
                                style={{
                                  color: "rgba(255,255,255,.55)",
                                  fontSize: ".75rem",
                                }}
                              >
                                {m.course_name}
                              </span>
                            </div>
                          ))
                        ) : (
                          <span
                            style={{
                              color: "rgba(255,255,255,.4)",
                              fontSize: ".75rem",
                            }}
                          >
                            {lbl("no_module", "No modules added yet")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {moduleCount > 0 ? (
                    <div className="hero-float-badge hero-float-badge--br">
                      <div className="hfb-icon">
                        <Icon.starFill style={{ fontSize: "13px" }} />
                      </div>
                      <div className="hfb-text">
                        <div className="hfb-label">{lbl("total", "Total")}</div>
                        <div className="hfb-val">
                          {moduleCount} {lbl("modules", "Modules")}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>
        <div className="hero-wave">
          <svg
            viewBox="0 0 1440 64"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
            height="64"
          >
            <path
              d="M0,32 C240,64 480,0 720,32 C960,64 1200,0 1440,32 L1440,64 L0,64 Z"
              fill="var(--ivory-2)"
            />
          </svg>
        </div>
      </section>

      {/* BENEFITS / WHAT YOU'LL LEARN */}
      {hasBenefits ? (
        <section className="benefits" id="benefits">
          <div className="container">
            {skill?.status && skill?.list?.length ? (
              <>
                <div className="benefits-header reveal">
                  <div className="section-label">
                    {lbl("what_youll_learn", "What You'll Learn")}
                  </div>
                  <h2 className="section-title">
                    {skill.heading || "What you'll learn"}
                  </h2>
                </div>
                <div className="grid-3">
                  {skill.list.map((s, i) => (
                    <div className="benefit-card reveal" key={i}>
                      <div className="benefit-desc">{s.pts || ""}</div>
                    </div>
                  ))}
                </div>
                {skill.long_desc ? (
                  <p
                    className="section-sub reveal"
                    style={{ margin: "2rem auto 0", textAlign: "center" }}
                  >
                    {String(skill.long_desc)
                      .split("\n")
                      .map((line, i, arr) => (
                        <span key={i}>
                          {line}
                          {i < arr.length - 1 ? <br /> : null}
                        </span>
                      ))}
                  </p>
                ) : null}
              </>
            ) : null}

            {course_include?.status && course_include?.list?.length ? (
              <div className="benefits-block">
                <div className="benefits-header reveal">
                  <div className="section-label">
                    {lbl("what_inside_course", "What's inside")}
                  </div>
                  <h2 className="section-title">
                    {course_include.heading || "This course includes"}
                  </h2>
                </div>
                <div className="grid-3">
                  {course_include.list.map((c, i) => (
                    <div className="benefit-card reveal" key={i}>
                      <div className="benefit-desc">{c.pts || ""}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {prerequisites?.status && prerequisites?.list?.length ? (
              <div className="benefits-block">
                <div className="benefits-header reveal">
                  <div className="section-label">
                    {lbl("prerequisites", "Prerequisites")}
                  </div>
                  <h2 className="section-title">
                    {prerequisites.heading || "Prerequisites"}
                  </h2>
                </div>
                <div className="grid-3">
                  {prerequisites.list.map((p, i) => (
                    <div className="benefit-card reveal" key={i}>
                      <div className="benefit-desc">{p.pts || ""}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* CURRICULUM */}
      {moduleCount && showCurriculum ? (
        <section className="curriculum" id="curriculum">
          <div className="container">
            <div className="curriculum-layout">
              <div className="curriculum-sticky reveal">
                <div className="curriculum-sticky-card">
                  <div className="curr-card-eyebrow">
                    {lbl("curriculum", "Curriculum")}
                  </div>
                  <div className="curr-card-title">
                    {lbl("inside_course", "What's inside the course")}
                  </div>
                  <div className="curr-card-stats">
                    <div className="curr-cs">
                      <div className="curr-cs-val">{moduleCount}</div>
                      <div className="curr-cs-label">
                        {lbl("modules", "modules")}
                      </div>
                    </div>
                    <div className="curr-cs">
                      <div className="curr-cs-val">{lessonCount}</div>
                      <div className="curr-cs-label">
                        {lbl("lessons", "lessons")}
                      </div>
                    </div>
                  </div>
                  <a
                    href={enrollLink}
                    className="btn btn--primary btn-cou-enroll btn-text-color"
                    style={{ width: "100%", justifyContent: "center" }}
                  >
                    <Icon.mortarboardFill className="me-2" />
                    {enrollText}
                  </a>
                </div>
              </div>

              <div>
                <div className="module-list">
                  {modules.map((m, idx) => {
                    const mls = lessonsFor(m.course_id);
                    const isOpen = openModule === idx;
                    return (
                      <div className="module-item" key={m.course_id}>
                        <div
                          className={`module-header${
                            isOpen ? "" : " collapsed"
                          }`}
                          aria-expanded={isOpen ? "true" : "false"}
                          style={{ cursor: "pointer" }}
                          onClick={() => setOpenModule(isOpen ? -1 : idx)}
                        >
                          <div className="module-num">
                            {String(idx + 1).padStart(2, "0")}
                          </div>
                          <div className="module-title">{m.course_name}</div>
                          {mls.length > 0 ? (
                            <div className="module-meta">
                              <span>
                                {mls.length} {lbl("lessons", "lessons")}
                              </span>
                            </div>
                          ) : (
                            <span className="module-tag">
                              {lbl("empty", "Empty")}
                            </span>
                          )}
                        </div>
                        <div className="module-collapse">
                          {isOpen ? (
                            <div className="module-lessons">
                              {mls.length ? (
                                mls.map((child) => (
                                  <div
                                    className="lesson-row"
                                    key={child.lesson_id}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "10px",
                                        flex: 1,
                                      }}
                                    >
                                      {Number(child.lesson_type || 0) === 0 ? (
                                        <Icon.fileEarmarkText
                                          className="lesson-icon"
                                          style={{ color: "var(--green)" }}
                                        />
                                      ) : (
                                        <Icon.playCircleFill
                                          className="lesson-icon"
                                          style={{ color: "var(--green)" }}
                                        />
                                      )}
                                      <span className="lesson-name">
                                        {String(
                                          child.lesson_name || "Lesson"
                                        ).slice(0, 100)}
                                      </span>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="no-lesson-found text-center py-3">
                                  <p>
                                    {lbl(
                                      "no_lesson_found",
                                      "No lessons found in this module"
                                    )}
                                  </p>
                                </div>
                              )}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* INSTRUCTOR */}
      {instructor && instructor.name ? (
        <section className="instructor" id="instructor">
          <div className="container">
            <div className="instructor-layout">
              <div className="instructor-portrait reveal">
                <div className="portrait-frame">
                  {instructor.profile_image ? (
                    <div className="portrait-inner">
                      <div className="portrait-image">
                        <img src={instructor.profile_image} alt="Instructor" />
                      </div>
                      <div className="portrait-name-badge">
                        <div className="portrait-name">{instructor.name}</div>
                        {instructor.location ? (
                          <div className="portrait-role">
                            {instructor.location}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <div className="portrait-inner">
                      <div className="portrait-placeholder">
                        {String(instructor.name || "")
                          .split(/\s+/)
                          .filter(Boolean)
                          .slice(0, 1)
                          .map((w) => w.charAt(0).toUpperCase())
                          .join("")}
                      </div>
                      <div className="portrait-name-badge">
                        <div className="portrait-name">{instructor.name}</div>
                        {instructor.location ? (
                          <div className="portrait-role">
                            {instructor.location}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="instructor-text reveal reveal-delay-2">
                <div className="section-label">
                  {lbl("meet_instructor", "Meet the Instructor")}
                </div>
                <h2 className="section-title section-title--light">
                  {instructor.name}
                </h2>
                <p className="instructor-bio">
                  {instructor.short_description || ""}
                </p>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* TESTIMONIALS */}
      {testimonial?.status && testimonial?.list?.length ? (
        <section className="testimonials" id="reviews">
          <div className="container">
            <div className="testimonials-header reveal">
              <div className="section-label">
                {lbl("testimonials", "Testimonials")}
              </div>
              <h2 className="section-title">
                {testimonial.heading || "What students are saying"}
              </h2>
            </div>
            <div className="testi-grid">
              {testimonial.list.map((t, i) => (
                <div className="testi-card reveal" key={i}>
                  <div className="testi-stars">
                    <Stars rating={t.rating ?? 5} />
                  </div>
                  <div className="testi-quote">{t.review || ""}</div>
                  <div className="testi-author">
                    <div className="testi-avatar">
                      {t.name
                        ? String(t.name).slice(0, 2).toUpperCase()
                        : "NA"}
                    </div>
                    <div>
                      <div className="testi-name">{t.name || ""}</div>
                      <div className="testi-role">{t.designation || ""}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* GUARANTEE */}
      {guaranteeVisible ? (
        <section className="guarantee-section" style={guaranteeStyle}>
          <div className="container">
            <div className="final-cta-inner">
              <h2 className="final-cta-title reveal">
                {accentWords(
                  money_back.heading || "30-Day Money-Back Guarantee",
                  "var(--amber)"
                )}
              </h2>
              {money_back.description ? (
                <div
                  className="final-cta-sub reveal"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(String(money_back.description).replace(
                      /\r\n|\r|\n/g,
                      "<br />"
                    )),
                  }}
                />
              ) : null}
            </div>
            <div className="guarantee-image">
              {money_back.money_back_image ? (
                <img
                  src={money_back.money_back_image}
                  alt="Guarantee"
                  className="img-fluid w-100"
                />
              ) : (
                <GuaranteeSeal />
              )}
            </div>
          </div>
        </section>
      ) : null}

      {/* FAQ */}
      {faq?.status && faq?.list?.length ? (
        <section className="faq" id="faq">
          <div className="container">
            <div className="faq-layout">
              <div className="faq-sticky">
                <div className="reveal">
                  <div className="section-label">{lbl("faq", "FAQ")}</div>
                  <h2 className="section-title">
                    {faq.heading || "Frequently asked questions"}
                  </h2>
                </div>
              </div>
              <div className="faq-list">
                {faq.list.map((f, index) => {
                  const isOpen = openFaq === index;
                  return (
                    <div
                      className={`faq-item${isOpen ? " open" : ""}`}
                      key={index}
                    >
                      <button
                        type="button"
                        className="faq-btn"
                        onClick={() => setOpenFaq(isOpen ? null : index)}
                      >
                        <span className="faq-question">{f.question || ""}</span>
                        <div className="faq-chevron">
                          <svg width="12" height="12" viewBox="0 0 12 12">
                            <path
                              d="M2 4l4 4 4-4"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </button>
                      <div className="faq-answer">
                        <div className="faq-answer-inner">{f.answer || ""}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* FINAL CTA */}
      <section className="final-cta">
        <div className="container">
          <div className="final-cta-inner">
            <h2 className="final-cta-title reveal">
              {accentWords(heroTitle, "var(--indigo)")}
            </h2>
            {heroSub ? <p className="final-cta-sub reveal">{heroSub}</p> : null}
            <div className="final-cta-actions reveal">
              <a
                href={enrollLink}
                className="btn btn--primary btn--primary-lg btn-nav-enroll btn-text-color"
                style={{ fontSize: "1.05rem", padding: "1.1rem 2.8rem" }}
              >
                {enrollText}
              </a>
            </div>
            <div className="final-cta-trust reveal">
              <div className="final-trust-item">
                <Icon.shieldCheck className="me-1" />
                {lbl("money_back_guarantee", "30-Day Money-Back Guarantee.") +
                  " " +
                  lbl(
                    "not_satisfied",
                    "Not satisfied? Full refund, no questions asked"
                  )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="site-footer">
        <div className="container">
          <div className="footer-bottom">
            <div className="footer-socials">
              {social.x_url ? (
                <a
                  href={social.x_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-social"
                >
                  <Icon.twitterX />
                </a>
              ) : null}
              {social.youtube_url ? (
                <a
                  href={social.youtube_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-social"
                >
                  <Icon.youtube />
                </a>
              ) : null}
              {social.linkedin_url ? (
                <a
                  href={social.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-social"
                >
                  <Icon.linkedin />
                </a>
              ) : null}
              {social.instagram_url ? (
                <a
                  href={social.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-social"
                >
                  <Icon.instagram />
                </a>
              ) : null}
              {social.facebook_url ? (
                <a
                  href={social.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-social"
                >
                  <Icon.facebook />
                </a>
              ) : null}
            </div>
            <div className="privacy-container">
              {hasPrivacy || hasTerms ? (
                <div className="footer-links">
                  {hasPrivacy ? (
                    <button type="button" onClick={() => setOpenPolicy("privacy")}>
                      {policies.privacy_subject || "Privacy Policy"}
                    </button>
                  ) : null}
                  {hasTerms ? (
                    <button type="button" onClick={() => setOpenPolicy("terms")}>
                      {policies.terms_subject || "Terms"}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </footer>

      <PolicyModal
        open={openPolicy}
        policies={policies}
        onClose={() => setOpenPolicy(null)}
      />
    </div>
  );
}
